import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { enqueue, drain, initSyncListeners } from '../lib/syncQueue';
import { saveWorkoutToHealth, isHealthWriteAvailable } from '../lib/enfernoHealthWorkouts';

const isNetworkError = (err) => {
  if (!err || err.response) return false;
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return true;
  return /network|fetch|failed to fetch|timeout/i.test(err.message || '');
};

const PROFILE_KEY = 'enferno_profile_v1';
const HISTORY_KEY = 'enferno_history_v1';
const TRAINING_KEY = 'enferno_training_v1';
// Snapshot of the most recent user_id that successfully synced to/from cloud
// in this localStorage. Used to detect cross-account boots (A logs out,
// B logs in on the same browser) — when the snapshot disagrees with the
// current `user.id`, we wipe local before the sync runs so A's history
// never leaks into B's cloud. See cross-account bleed RCA.
const LAST_SYNCED_USER_KEY = 'enferno_last_synced_user_id_v1';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROFILE_DEBOUNCE_MS = 800;

// ---------- Paginated fetch helpers ----------
// The cloud sync MUST hydrate the full dataset (otherwise older entries are
// treated as "local-only" and re-pushed as dupes). The backend caps each
// response at 500; we loop until `has_more` is false.
const PAGE_LIMIT = 500;

async function fetchAllWorkouts() {
  const all = [];
  let historyClamped = false;
  let historyClampDays = null;
  let before = null;
  // Hard cap on iterations as a safety net (250k entries before this fails).
  for (let i = 0; i < 500; i += 1) {
    const params = new URLSearchParams({ limit: String(PAGE_LIMIT) });
    if (before) params.set('before', before);
    const res = await axios.get(`${API}/workouts?${params.toString()}`);
    const items = res.data?.workouts || [];
    historyClamped = res.data?.history_clamped || false;
    historyClampDays = res.data?.history_clamp_days ?? historyClampDays;
    all.push(...items);
    if (!res.data?.has_more || !res.data?.next_before) break;
    before = res.data.next_before;
  }
  return { items: all, historyClamped, historyClampDays };
}

async function fetchAllTrainingSessions() {
  const all = [];
  let before = null;
  for (let i = 0; i < 500; i += 1) {
    const params = new URLSearchParams({ limit: String(PAGE_LIMIT) });
    if (before) params.set('before', before);
    const res = await axios.get(`${API}/training-sessions?${params.toString()}`);
    const items = res.data?.sessions || [];
    all.push(...items);
    if (!res.data?.has_more || !res.data?.next_before) break;
    before = res.data.next_before;
  }
  return { items: all };
}

// Migrations for renamed IDs across versions (recovery activities + equipment)
const RECOVERY_ID_MAP = {
  walking: 'walk-run',
  'stationary-bike': 'cycling',
  elliptical: 'elliptical-max',
};
const EQUIPMENT_ID_MAP = {
  elliptical: 'elliptical-max',
  'max-trainer': 'elliptical-max',
};

const migrateProfile = (raw) => {
  const p = { ...raw };
  if (Array.isArray(p.recoveryActivities)) {
    p.recoveryActivities = Array.from(new Set(p.recoveryActivities.map((id) => RECOVERY_ID_MAP[id] || id)));
  }
  if (Array.isArray(p.equipment)) {
    p.equipment = Array.from(new Set(p.equipment.map((id) => EQUIPMENT_ID_MAP[id] || id)));
  }
  return p;
};

const defaultProfile = {
  completedOnboarding: false,
  name: '',
  email: '',
  // Public-facing handle used on share cards and (with opt-in) for any future
  // social recognition, leaderboard, or contest features. Prefixed with `@`
  // automatically when rendered. Empty = card uses name as fallback.
  socialHandle: '',
  age: '',
  sex: '',
  heightFt: '',
  heightIn: '',
  weight: '',
  experience: '',
  goals: [],
  daysPerWeek: 4,
  minutesPerSession: 45,
  // Rest between sets, in seconds. Flat, user-overridable default (founder
  // spec Jul 2026) — 90s covers the large majority of our general-population
  // users. Raising it trades a longer real session time for more rest; sets/
  // exercises are what flex to protect the advertised session length, not this.
  restSeconds: 90,
  injuries: {},
  injuryNotes: '',
  fullGym: false,
  noEquipment: false,
  equipment: [],
  recoveryActivities: [],
  martialArts: [],
  ranks: {},
  // Per-art promotion history: { [artId]: [{ id, rankId, stripes, promotedAt, school?, by?, photo?, notes? }] }
  // promotedAt is an ISO date string 'YYYY-MM-DD' so the calendar groups cleanly
  // regardless of TZ. Entries are sorted ascending by date when rendered.
  promotionLog: {},
  // Paid tier flag — flips to true when a user upgrades. Currently always false
  // since the paid tier isn't built. Gates: watermark-free share cards. Future:
  // any feature that the founder wants to wall behind paid tier.
  premium: false,
  // ---- Streak system (adherence-first; see utils/streaks.js) ----
  // User-defined calendar pauses: [{ id, start: 'YYYY-MM-DD', end: 'YYYY-MM-DD'|null, reason?, note? }]
  // No app-imposed length limit; user knows their body.
  streakPauses: [],
  // Highest streak length ever achieved (days). Never resets.
  streakBestEver: 0,
  // Highest threshold value (in days) we've already celebrated, so we don't
  // re-surface the same milestone every time the user opens Strength.
  streakLastCelebratedDays: 0,
  // Program completion log: [{ id, programId, startedAt: ISO, completedAt: ISO, weeks }]
  // Two consecutive completions (gap < 30d) unlocks Expert tier.
  programCompletions: [],
  // When the user picks "Not yet — extending" on the completion prompt, snooze
  // re-prompting for 7 days so they aren't pestered every session.
  programCompletionSnoozedUntil: null,
  programType: 'upper-lower',
  startDate: '',
  schedule: null,
  painLog: {},
  nutritionGoal: null,
  activityLevel: 'moderate',
  foodLog: {},
  // ----- Path B: Adaptive Mode (opt-in, niche pillars) -----
  adaptiveMode: false,
  adaptiveSubmodes: [], // ['chronic-pain', 'amputee', 'cycle', 'perimenopause', 'older-adult']
  // ----- Prosthesis variants (amputee submode users only) -----
  // Multi-select. A user may have a day-prosthesis + sport-blade + crutches
  // simultaneously. Maps to library exercise tags via data/prosthesisVariants.js.
  prosthesisVariants: [],
  // ----- Holistic check-in log (Anti-Shame UX: non-scale victories) -----
  wellbeingLog: {}, // { 'YYYY-MM-DD': { energy: 1-5, sleep: 1-5, mood: 1-5, function: 1-5, ts } }
  // ----- Display preferences (which widgets show on each page) -----
  displayPrefs: { preset: 'default' }, // { preset: 'default'|'simplified', [page]: { [widgetId]: bool } }
  // ----- Contextual prompts: tracks which first-use surveys the user has seen -----
  seenContextualPrompts: {}, // { 'injuries-first-pain-check': true, 'equipment-first-tap': 'dismissed', ... }
  // ----- Beta participation acknowledgement (ISO timestamp or null) -----
  // Captured at register-time via Register.jsx → localStorage; lifted into
  // the profile on first hydrate so it syncs to the backend.
  betaTermsAcceptedAt: null,
  // ----- Recognition / Leaderboards (Layer 1 — per-card consent) -----
  // [{ cardId, type, grantedAt }] — see addAmplificationConsent for details.
  amplificationConsents: [],
  // ----- Share-card referral attribution -----
  // If the user landed on /welcome/:context before registering, the context
  // slug ('promotion' | 'streak' | 'pr' | 'sessions') is stashed in
  // localStorage and lifted here on first hydrate. Helps measure which share
  // surface drives the most installs once amplification queues are running.
  referredFrom: null,
  // ----- HealthKit V2: workout write-back toggle (off by default) -----
  // When true + native + write permission granted, every Enferno-logged
  // workout is pushed to Apple Health / Health Connect as an HKWorkout /
  // ExerciseSessionRecord. Failures are silent (workout still lands in
  // Enferno's own DB). Per brand axiom: the user owns the toggle.
  healthkitWriteBackEnabled: false,
  // ----- Units preference -----
  // Drives GPS distance + splits + pace display. Strength weight units stay
  // implicit (lb) for now; can branch to kg here when that's tackled.
  units: 'imperial', // 'imperial' (mi) | 'metric' (km)
  // ----- Nutrition: alcohol gate + saved meals -----
  // showAlcohol: hide alcohol entries from the food picker by default. User
  // can flip on in Nutrition settings. Surfaces at the store-listing level
  // means the app doesn't ship as 17+-content-by-default.
  showAlcohol: false,
  // savedMeals: user-defined templates of {foodId, servings, meal} groups —
  // load as a single tap when re-eating the same meal.
  savedMeals: [],
};

const defaultTraining = { sessions: [] };

const ProfileContext = createContext({
  profile: defaultProfile,
  history: [],
  training: defaultTraining,
  update: () => {},
  reset: () => {},
  completeWorkout: () => {},
  updateWorkout: () => {},
  resetTrainingHistory: async () => ({ ok: true }),
  addTrainingSession: () => {},
  deleteTrainingSession: () => {},
  updateTrainingSession: () => {},
  updateRank: () => {},
  addPromotion: () => {},
  deletePromotion: () => {},
  logPain: () => {},
  loaded: false,
});

const isMeaningfulProfile = (p) => !!(p && (p.completedOnboarding || p.name || p.email));

export const ProfileProvider = ({ children }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(defaultProfile);
  const [history, setHistory] = useState([]);
  const [training, setTraining] = useState(defaultTraining);
  const [historyClamped, setHistoryClamped] = useState(false);
  const [historyClampDays, setHistoryClampDays] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle' | 'syncing' | 'conflict' | 'error'

  const profileVersion = useRef(0);
  const lastSyncedUserId = useRef(null);
  const profilePushTimer = useRef(null);

  // ---------- Initial localStorage hydrate ----------
  useEffect(() => {
    try {
      const rawP = localStorage.getItem(PROFILE_KEY);
      if (rawP) setProfile(migrateProfile({ ...defaultProfile, ...JSON.parse(rawP) }));
      const rawH = localStorage.getItem(HISTORY_KEY);
      if (rawH) setHistory(JSON.parse(rawH));
      const rawT = localStorage.getItem(TRAINING_KEY);
      if (rawT) setTraining({ ...defaultTraining, ...JSON.parse(rawT) });
    } catch { /* ignore */ }
    // Founder dev toggle (per STANDING_INSTRUCTIONS) — URL `?dev_premium=1`
    // flips the premium flag for the current device. Persists in localStorage
    // so the toggle survives reloads. `?dev_premium=0` clears it. No visible UI.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('dev_premium') === '1') {
        localStorage.setItem('enferno-dev-premium', '1');
        setProfile((p) => ({ ...p, premium: true }));
      } else if (params.get('dev_premium') === '0') {
        localStorage.removeItem('enferno-dev-premium');
        setProfile((p) => ({ ...p, premium: false }));
      } else if (localStorage.getItem('enferno-dev-premium') === '1') {
        setProfile((p) => ({ ...p, premium: true }));
      }
    } catch { /* ignore */ }
    // Beta participation acceptance lift — Register.jsx writes to localStorage
    // at register-time because ProfileContext may not be mounted yet. Lift it
    // into the profile here so it syncs to the backend on next profile PUT.
    try {
      const betaAcceptedAt = localStorage.getItem('enferno_beta_terms_accepted_at');
      if (betaAcceptedAt) {
        setProfile((p) => p.betaTermsAcceptedAt ? p : { ...p, betaTermsAcceptedAt: betaAcceptedAt });
      }
    } catch { /* ignore */ }
    // Share-card referral lift — set by /welcome/:context on visit.
    // Only lifts ONCE (only if profile doesn't already have a referredFrom),
    // and we clear the localStorage key after lift so subsequent scans don't
    // overwrite the original attribution.
    try {
      const shareRef = localStorage.getItem('enferno_share_ref');
      if (shareRef) {
        setProfile((p) => {
          if (p.referredFrom) {
            // Already attributed — discard the new scan.
            try { localStorage.removeItem('enferno_share_ref'); } catch { /* ignore */ }
            return p;
          }
          try { localStorage.removeItem('enferno_share_ref'); } catch { /* ignore */ }
          return { ...p, referredFrom: shareRef };
        });
      }
    } catch { /* ignore */ }
    setLoaded(true);
    // Wire offline sync queue: listens for `online` events + drains anything
    // pending on app startup. Idempotent — safe to call once per provider mount.
    initSyncListeners();
  }, []);

  // ---------- localStorage cache (always on, offline fallback) ----------
  useEffect(() => { if (loaded) localStorage.setItem(PROFILE_KEY, JSON.stringify(profile)); }, [profile, loaded]);
  useEffect(() => { if (loaded) localStorage.setItem(HISTORY_KEY, JSON.stringify(history)); }, [history, loaded]);
  useEffect(() => {
    if (!loaded) return;
    try { localStorage.setItem(TRAINING_KEY, JSON.stringify(training)); }
    catch (e) { console.warn('Training storage full — consider pruning attachments.', e); }
  }, [training, loaded]);

  // ---------- Cloud sync: full pull on login + bidirectional merge with localStorage ----------
  // CRITICAL: never blindly replace local history/training with cloud data — entries that
  // exist locally but not in cloud (failed POST, race condition, offline log) would be lost.
  // Instead, merge by client_id: cloud entries hydrate local, local-only entries get pushed up.
  //
  // CROSS-ACCOUNT BLEED GUARD: if the localStorage was last synced
  // under a different user_id (e.g. user A logged out, user B logged in on
  // the same browser), wipe local state BEFORE the merge runs — otherwise
  // A's training/history would be treated as B's "local-only" and pushed up
  // into B's cloud. See `cleanup_training_session_dupes.py` for the postmortem.
  useEffect(() => {
    if (!loaded) return;
    if (!user) {
      lastSyncedUserId.current = null;
      profileVersion.current = 0;
      return;
    }
    if (lastSyncedUserId.current === user.id) return;
    lastSyncedUserId.current = user.id;

    // Detect cross-account boot. The snapshot is written AFTER a successful sync,
    // so a missing snapshot on first login is treated as "first sync ever" (no wipe).
    let crossAccountBoot = false;
    let localProfileSnapshot = profile;
    let localHistorySnapshot = history;
    let localTrainingSnapshot = training;
    try {
      const lastUserId = localStorage.getItem(LAST_SYNCED_USER_KEY);
      if (lastUserId && lastUserId !== user.id) {
        crossAccountBoot = true;
        console.warn(`[ProfileContext] Cross-account boot detected: last=${lastUserId} current=${user.id}. Wiping local cache.`);
        // Reset in-memory state to defaults — the cloud sync below will hydrate
        // the new user's actual data into a clean slate.
        setProfile(defaultProfile);
        setHistory([]);
        setTraining(defaultTraining);
        localProfileSnapshot = defaultProfile;
        localHistorySnapshot = [];
        localTrainingSnapshot = defaultTraining;
        // Wipe localStorage too so a refresh during sync doesn't re-hydrate A's data.
        try { localStorage.removeItem(PROFILE_KEY); } catch { /* ignore */ }
        try { localStorage.removeItem(HISTORY_KEY); } catch { /* ignore */ }
        try { localStorage.removeItem(TRAINING_KEY); } catch { /* ignore */ }
      }
    } catch { /* ignore — fall through to normal sync */ }

    (async () => {
      try {
        setSyncStatus('syncing');
        const [profRes, workResp, sessResp] = await Promise.all([
          axios.get(`${API}/profile`),
          fetchAllWorkouts(),
          fetchAllTrainingSessions(),
        ]);

        const cloudProfile = profRes.data.profile || {};
        profileVersion.current = profRes.data.version || 0;
        const cloudWorkouts = workResp.items;
        const cloudSessions = sessResp.items;

        // G3: track if the server clamped history due to free tier
        const clamped = !!workResp.historyClamped;
        const clampDays = workResp.historyClampDays || null;
        setHistoryClamped(clamped);
        setHistoryClampDays(clampDays);

        const cloudHasProfile = isMeaningfulProfile(cloudProfile);
        // On a cross-account boot, local snapshots have already been reset to
        // defaults — so localHasProfile is `false` and we correctly skip
        // pushing A's profile into B's account.
        const localHasProfile = isMeaningfulProfile(localProfileSnapshot);

        // --- Profile: cloud wins if it has content, otherwise push local up. ---
        if (cloudHasProfile) {
          setProfile(migrateProfile({ ...defaultProfile, ...cloudProfile }));
        } else if (localHasProfile) {
          await axios.put(`${API}/profile`, { data: localProfileSnapshot }, {
            headers: { 'If-Match': String(profileVersion.current) },
          }).then((r) => {
            profileVersion.current = r.data.version || profileVersion.current + 1;
          }).catch((e) => console.warn('Profile migration push failed:', e?.message));
        }

        // --- Workouts: bidirectional merge by client_id. ---
        const cloudWorkoutIds = new Set(cloudWorkouts.map((w) => w.id).filter(Boolean));
        const cloudWorkoutClientIds = new Set(cloudWorkouts.map((w) => w.client_id).filter(Boolean));
        const localOnlyWorkouts = localHistorySnapshot.filter((w) => {
          const cid = w.client_id || w.id;
          if (!cid) return false;
          // Skip if either the server id OR the stable client_id is already in cloud.
          if (w.id && cloudWorkoutIds.has(w.id)) return false;
          if (cloudWorkoutClientIds.has(cid)) return false;
          return true;
        });
        // Push local-only entries up so cloud catches up. Use the stable
        // client_id (the `tr-*` / `wo-*` value from creation) — using the
        // mongo id here would defeat server-side idempotency and create
        // duplicates on every sync.
        await Promise.all(
          localOnlyWorkouts.map((w) => axios.post(`${API}/workouts`, { client_id: w.client_id || w.id, data: w })
            .catch((e) => console.warn('Workout merge-push failed', w.id, e?.message))),
        );
        // Final state = union, sorted by date desc
        const mergedWorkouts = [...cloudWorkouts, ...localOnlyWorkouts]
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setHistory(mergedWorkouts);

        // --- Training sessions: same bidirectional merge. ---
        const cloudSessionIds = new Set(cloudSessions.map((s) => s.id).filter(Boolean));
        const cloudSessionClientIds = new Set(cloudSessions.map((s) => s.client_id).filter(Boolean));
        // Tombstones: the server tracks every deleted session's
        // client_id and _id for this user. We filter local-only sessions
        // against the tombstone set BEFORE re-uploading — otherwise a
        // stale localStorage on another device resurrects deletions on
        // every login. See `/app/memory/CHANGELOG.md` for the RCA.
        let tombstoneClientIds = new Set();
        let tombstoneSessionIds = new Set();
        try {
          const tRes = await axios.get(`${API}/training-sessions/tombstones`);
          tombstoneClientIds = new Set(tRes.data?.client_ids || []);
          tombstoneSessionIds = new Set(tRes.data?.session_ids || []);
        } catch (e) {
          // Non-fatal: if the tombstone endpoint fails, the merge-up
          // continues as before. Worst case is the user sees a few
          // resurrected sessions which they can re-delete.
          console.warn('Tombstone fetch failed, proceeding without filter', e?.message);
        }
        const localOnlySessions = (localTrainingSnapshot.sessions || []).filter((s) => {
          const cid = s.client_id || s.id;
          if (!cid) return false;
          if (s.id && cloudSessionIds.has(s.id)) return false;
          if (cloudSessionClientIds.has(cid)) return false;
          // Tombstoned on the cloud: skip silently — this session was
          // deleted and the cache is stale.
          if (s.id && tombstoneSessionIds.has(s.id)) return false;
          if (tombstoneClientIds.has(cid)) return false;
          return true;
        });
        await Promise.all(
          localOnlySessions.map((s) => axios.post(`${API}/training-sessions`, { client_id: s.client_id || s.id, data: s })
            .catch((e) => {
              // 410 Gone = server-side tombstone hit. Treat as success
              // (the row is correctly absent) so the queue doesn't keep
              // retrying a permanently-dead push.
              if (e?.response?.status !== 410) {
                console.warn('Session merge-push failed', s.id, e?.message);
              }
            })),
        );
        const mergedSessions = [...cloudSessions, ...localOnlySessions]
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setTraining({ sessions: mergedSessions });

        // Mark this user_id as the one currently owning the localStorage cache.
        try { localStorage.setItem(LAST_SYNCED_USER_KEY, user.id); } catch { /* ignore */ }

        setSyncStatus('idle');
        if (crossAccountBoot) {
          // Anti-shame, plain-language toast — let the user know the local
          // cache was reset (so they don't think they lost data).
          toast.success("Switched accounts — pulled your data fresh.", { duration: 3500 });
        }
        // After login pull, replay anything in the offline queue under the now-authenticated session
        drain().catch(() => { /* swallow — listener will retry on next online event */ });
      } catch (err) {
        console.warn('Cloud sync pull failed:', err?.message || err);
        setSyncStatus('error');
        // Silent toast — non-blocking, anti-shame framing
        toast.error('Sync paused', {
          description: "Working offline. We'll catch up next time you're online.",
          duration: 4500,
        });
      }
    })();
  }, [user, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Profile push: debounced, SERIALIZED, versioned, non-destructive ----------
  // Latest-profile ref so a serialized/queued push always sends current state,
  // never a stale closure snapshot.
  const profileRef = useRef(profile);
  useEffect(() => { profileRef.current = profile; }, [profile]);

  // Only ONE profile PUT may be in flight at a time. Overlapping pushes under
  // network latency (app → Atlas) were sending stale `If-Match` versions, which
  // the server rejected with 409 — and the old handler then overwrote local
  // state with the server copy, silently discarding unsynced changes (e.g. a
  // just-finished `completedOnboarding`). Serializing kills the race at its
  // source; the 409 path now merges instead of clobbering.
  const pushInFlight = useRef(false);
  const pushQueued = useRef(false);

  const flushProfile = useCallback(async function flush() {
    if (!user) return;
    if (pushInFlight.current) { pushQueued.current = true; return; }
    pushInFlight.current = true;
    const url = `${API}/profile`;
    const snapshot = profileRef.current;
    const headers = { 'If-Match': String(profileVersion.current) };
    try {
      const res = await axios.put(url, { data: snapshot }, { headers });
      profileVersion.current = res.data.version || profileVersion.current + 1;
      setSyncStatus('idle');
    } catch (err) {
      if (err?.response?.status === 409) {
        const cloud = err.response.data?.detail;
        if (cloud?.profile) {
          profileVersion.current = cloud.current_version || 0;
          // Non-destructive: cloud as base, local unsynced changes win on top,
          // so a conflict can't silently revert what the user just did. This
          // setProfile re-arms the debounce, which re-pushes the merged result
          // with the now-correct version.
          setProfile((local) => migrateProfile({ ...defaultProfile, ...cloud.profile, ...local }));
          setSyncStatus('conflict');
          console.warn('Profile conflict — merged local over cloud version', cloud.current_version);
        }
      } else if (isNetworkError(err)) {
        // Offline → queue for later replay. A version-less re-push on drain
        // reconciles against whatever the server holds then.
        await enqueue({ method: 'put', url, body: { data: snapshot }, headers }).catch(() => { /* idb error */ });
      } else {
        console.warn('Profile push failed:', err?.message);
      }
    } finally {
      pushInFlight.current = false;
      // Drain any change that arrived while this push was in flight, using the
      // latest profile + the freshly-updated version.
      if (pushQueued.current) {
        pushQueued.current = false;
        flush();
      }
    }
  }, [user]);

  useEffect(() => {
    if (!loaded || !user) return;
    if (profilePushTimer.current) clearTimeout(profilePushTimer.current);
    profilePushTimer.current = setTimeout(() => { flushProfile(); }, PROFILE_DEBOUNCE_MS);
    return () => profilePushTimer.current && clearTimeout(profilePushTimer.current);
  }, [profile, user, loaded, flushProfile]);

  // ---------- Apply body[data-mode] for Adaptive Mode palette swap ----------
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (profile.adaptiveMode) {
      document.body.setAttribute('data-mode', 'adaptive');
    } else {
      document.body.removeAttribute('data-mode');
    }
  }, [profile.adaptiveMode]);

  const update = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const reset = () => {
    localStorage.removeItem(PROFILE_KEY);
    localStorage.removeItem(HISTORY_KEY);
    localStorage.removeItem(TRAINING_KEY);
    // Clear the onboarding draft so stale answers don't pre-fill the reset flow.
    localStorage.removeItem('enferno_onboarding_draft_v1');
    // Clear the AI consent notice flag so the first-interaction notice re-fires.
    localStorage.removeItem('enferno_ai_consent_shown_v1');
    setProfile(defaultProfile);
    setHistory([]);
    setTraining(defaultTraining);
    // Do NOT reset profileVersion — keep the current version so the next
    // debounced flush sends If-Match: N (which matches the cloud), succeeds, and
    // overwrites the cloud with defaultProfile. Resetting to 0 causes a 409
    // conflict whose handler would restore onboardingStepsAnswered from the old
    // cloud profile, breaking the Reset → re-onboard flow.
  };

  // ---------- Training-history-only reset ----------
  // Wipes server-side workouts + training_sessions for the current user AND
  // clears the local mirror. Profile / preferences / equipment / streaks /
  // pain log all stay intact. For beta testers who accumulate nonsense test
  // data and want a clean analytics slate without losing setup.
  const resetTrainingHistory = async () => {
    // Local clear is immediate so the UI reflects the wipe even if the
    // cloud call lags or fails (next sync will reconcile).
    setHistory([]);
    setTraining(defaultTraining);
    try { localStorage.removeItem(HISTORY_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(TRAINING_KEY); } catch { /* ignore */ }
    if (user) {
      try {
        const res = await axios.delete(`${API}/profile/training-history`);
        return res.data;  // { ok, workouts_deleted, training_sessions_deleted }
      } catch (e) {
        console.warn('Training history cloud reset failed:', e?.message);
        return { ok: false, error: e?.message };
      }
    }
    return { ok: true, workouts_deleted: 0, training_sessions_deleted: 0 };
  };

  // ---------- Per-entry workout sync ----------
  // Returns the client record so callers can route off it. Cloud POST is fire-and-forget
  // but on failure the entry is retained locally — the next bidirectional sync will push it.
  const completeWorkout = (entry) => {
    // Honor entry.id when the caller provided one (GPS sessions save raw points
    // keyed by id, so the caller mints the id ahead of time). Otherwise mint.
    const clientId = entry.id || `wk-${Date.now()}`;
    // Honor entry.date if provided (historical logging), otherwise default to now
    const record = { ...entry, id: clientId, date: entry.date || new Date().toISOString() };
    setHistory((h) => [record, ...h]);

    // ----- HealthKit V2 write-back (mobile + opt-in only) -----
    // Best-effort fire-and-forget. Workout already persisted locally + queued
    // to cloud; this just mirrors it into Apple Health / Health Connect so
    // the user's wearable ecosystem sees it. Failures are silent.
    if (profile.healthkitWriteBackEnabled && isHealthWriteAvailable()) {
      const durationMin = Number(entry.duration) || 45;
      const endDate = new Date(record.date);
      const startDate = new Date(endDate.getTime() - durationMin * 60 * 1000);
      const activityHint = (entry.programType || entry.discipline || 'strength').toLowerCase();
      saveWorkoutToHealth({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        activityType: activityHint,
        caloriesKcal: entry.caloriesKcal,
        metadata: {
          enfernoWorkoutId: clientId,
          title: entry.title || '',
        },
      }).catch(() => { /* silent — workout still saved locally */ });
    }

    if (user) {
      const url = `${API}/workouts`;
      const body = { client_id: clientId, data: record };
      const tryPush = (attempt = 0) =>
        axios.post(url, body)
          .then((r) => {
            const server = r.data?.workout;
            if (server) {
              setHistory((h) => h.map((w) => (w.id === clientId ? { ...w, ...server } : w)));
            }
          })
          .catch((e) => {
            if (isNetworkError(e)) {
              // Tag the local history entry so the OFFLINE badge appears immediately,
              // even before the queue drains. Survives the cloud merge later because
              // the server stores the same flag on replay (see syncQueue.drain).
              setHistory((h) => h.map((w) => (w.id === clientId
                ? { ...w, offline_queued: true, original_ts: new Date().toISOString() }
                : w)));
              // Offline → push to queue. Optimistic UI already shows the workout;
              // drain() will replay POST when connectivity returns. client_id keeps it idempotent.
              enqueue({ method: 'post', url, body }).catch(() => { /* idb error — log */ });
              return;
            }
            if (attempt < 2) {
              // Server error (5xx etc.) — quick retry with backoff for transient blips
              setTimeout(() => tryPush(attempt + 1), 1500 * Math.pow(2, attempt));
            } else {
              console.warn('Workout cloud sync failed after retries — will reconcile on next sync:', e?.message);
            }
          });
      tryPush();
    }
    return record;
  };

  // ---------- Workout deletion ----------
  // Local-first delete. Mirrors the cloud delete fire-and-forget pattern of
  // completeWorkout — if the user is online and we have a server-side row,
  // best-effort DELETE; if offline, queue it. Optimistic UI removes the entry
  // immediately so the user gets feedback.
  const removeWorkout = (workoutId) => {
    if (!workoutId) return;
    setHistory((h) => h.filter((w) => w.id !== workoutId));
    if (user) {
      const url = `${API}/workouts/${workoutId}`;
      axios.delete(url).catch((e) => {
        if (isNetworkError(e)) {
          enqueue({ method: 'delete', url, body: null }).catch(() => { /* idb error */ });
        } else {
          console.warn('Workout delete cloud sync failed:', e?.message);
        }
      });
    }
  };

  // ---------- Workout patch (edit) ----------
  // Local-first edit. Optimistic UI updates the entry immediately. Cloud PATCH
  // is fire-and-forget; offline, the request is queued and replayed by drain().
  // `patch` is a partial object — whitelisted server-side to title/date/duration/
  // totalVolume/exercisesCompleted/exerciseCount/notes.
  const updateWorkout = (workoutId, patch) => {
    if (!workoutId || !patch || Object.keys(patch).length === 0) return;
    setHistory((h) => h.map((w) => (w.id === workoutId ? { ...w, ...patch } : w)));
    if (user) {
      const url = `${API}/workouts/${workoutId}`;
      axios.patch(url, patch)
        .then((r) => {
          const server = r.data?.workout;
          if (server) {
            setHistory((h) => h.map((w) => (w.id === workoutId ? { ...w, ...server } : w)));
          }
        })
        .catch((e) => {
          if (isNetworkError(e)) {
            enqueue({ method: 'patch', url, body: patch }).catch(() => { /* idb error */ });
          } else {
            console.warn('Workout edit cloud sync failed:', e?.message);
          }
        });
    }
  };


  // ---------- Per-entry training session sync ----------
  //
  // Cloud-sync filter: attachments may carry device-local fields (localUri,
  // persistedPath, dataUrl from legacy code) that must NEVER leave the
  // device. localUri is a Capacitor file:// URI or an ephemeral Blob URL,
  // useless on any other device. dataUrl is a fat base64 blob — historic
  // shape we've now retired. Strip both at the cloud boundary.
  const stripLocalAttachmentFields = (record) => {
    if (!record?.attachments) return record;
    return {
      ...record,
      attachments: record.attachments.map((a) => {
        const { localUri, persistedPath, dataUrl, ...rest } = a;  // eslint-disable-line no-unused-vars
        return rest;
      }),
    };
  };

  const addTrainingSession = (entry) => {
    const clientId = `tr-${Date.now()}`;
    const record = { id: clientId, date: new Date().toISOString(), ...entry };
    setTraining((t) => ({ ...t, sessions: [record, ...t.sessions] }));
    if (user) {
      const url = `${API}/training-sessions`;
      const body = { client_id: clientId, data: stripLocalAttachmentFields(record) };
      axios.post(url, body)
        .then((r) => {
          const server = r.data?.session;
          if (server) {
            setTraining((t) => ({
              ...t,
              sessions: t.sessions.map((s) => (s.id === clientId ? { ...s, ...server } : s)),
            }));
          }
        })
        .catch((e) => {
          if (isNetworkError(e)) {
            // Tag local entry for OFFLINE badge — mirrors workout logic.
            setTraining((t) => ({
              ...t,
              sessions: t.sessions.map((s) => (s.id === clientId
                ? { ...s, offline_queued: true, original_ts: new Date().toISOString() }
                : s)),
            }));
            enqueue({ method: 'post', url, body }).catch(() => { /* idb error */ });
          } else {
            console.warn('Training session cloud sync failed:', e?.message);
          }
        });
    }
    return record;
  };

  const deleteTrainingSession = (id) => {
    setTraining((t) => ({ ...t, sessions: t.sessions.filter((s) => s.id !== id) }));
    if (user) {
      axios.delete(`${API}/training-sessions/${id}`)
        .catch((e) => console.warn('Training session delete failed:', e?.message));
    }
  };

  // Update an existing training session with new data. The `id` stays the
  // same; `data` is shallow-merged onto the existing record. Used by the
  // BJJ detailed-journal Edit flow. Cloud sync mirrors the update through
  // the existing POST endpoint (server treats it as an upsert by client_id).
  const updateTrainingSession = (id, patch) => {
    let updated = null;
    let target = null;
    setTraining((t) => ({
      ...t,
      sessions: t.sessions.map((s) => {
        if (s.id !== id) return s;
        target = s;
        updated = { ...s, ...patch };
        return updated;
      }),
    }));
    if (user && updated) {
      // PATCH the existing record by its server identity (mongo id or
      // stable client_id — backend matches either). Using POST here
      // historically created a duplicate on every edit because the server
      // idempotency key only matches the stored `client_id` field, not
      // the mongo id we were passing as a stand-in.
      const url = `${API}/training-sessions/${id}`;
      const body = { client_id: target?.client_id || id, data: stripLocalAttachmentFields(updated) };
      axios.patch(url, body)
        .catch((e) => {
          if (isNetworkError(e)) {
            enqueue({ method: 'patch', url, body }).catch(() => { /* idb error */ });
          } else {
            console.warn('Training session update cloud sync failed:', e?.message);
          }
        });
    }
    return updated;
  };

  const updateRank = (artId, rankId, stripes) => {
    setProfile((p) => ({ ...p, ranks: { ...p.ranks, [artId]: { rankId, stripes: stripes ?? 0 } } }));
  };

  // Append a promotion event to the log AND set it as the current rank.
  // entry: { rankId, stripes?, promotedAt (YYYY-MM-DD), school?, by?, photo?, notes? }
  // Generates a stable id; idempotent on caller-side (UI prevents double-submit).
  const addPromotion = (artId, entry) => {
    const id = `pr_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const full = { id, ...entry };
    setProfile((p) => {
      const log = { ...(p.promotionLog || {}) };
      log[artId] = [...(log[artId] || []), full];
      return {
        ...p,
        promotionLog: log,
        ranks: { ...p.ranks, [artId]: { rankId: entry.rankId, stripes: entry.stripes ?? 0 } },
      };
    });
  };

  const deletePromotion = (artId, promotionId) => {
    setProfile((p) => {
      const log = { ...(p.promotionLog || {}) };
      log[artId] = (log[artId] || []).filter((e) => e.id !== promotionId);
      return { ...p, promotionLog: log };
    });
  };

  // ----- Streak pauses (user-defined calendar pauses, no app-imposed limit) -----
  const addStreakPause = ({ start, end = null, reason = '', note = '' }) => {
    const id = `pause_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setProfile((p) => ({
      ...p,
      streakPauses: [...(p.streakPauses || []), { id, start, end, reason, note }],
    }));
    return id;
  };
  const closeStreakPause = (pauseId, endISO) => {
    setProfile((p) => ({
      ...p,
      streakPauses: (p.streakPauses || []).map((x) => x.id === pauseId ? { ...x, end: endISO } : x),
    }));
  };
  const removeStreakPause = (pauseId) => {
    setProfile((p) => ({
      ...p,
      streakPauses: (p.streakPauses || []).filter((x) => x.id !== pauseId),
    }));
  };
  const markStreakCelebrated = (days) => {
    setProfile((p) => ({ ...p, streakLastCelebratedDays: Math.max(p.streakLastCelebratedDays || 0, days) }));
  };
  const updateStreakBestEver = (days) => {
    setProfile((p) => ({ ...p, streakBestEver: Math.max(p.streakBestEver || 0, days) }));
  };
  const addProgramCompletion = ({ programId, startedAt, completedAt, weeks }) => {
    const id = `prog_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setProfile((p) => ({
      ...p,
      programCompletions: [...(p.programCompletions || []), { id, programId, startedAt, completedAt, weeks }],
      // Clear snooze on confirm — this run is closed.
      programCompletionSnoozedUntil: null,
    }));
    return id;
  };

  // Snooze the program-completion prompt for 7 days so the user isn't pestered
  // every session if they're extending a program past its target length.
  const snoozeProgramCompletion = () => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    const until = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setProfile((p) => ({ ...p, programCompletionSnoozedUntil: until }));
  };

  // Per-card amplification consent (Recognition Layer 1).
  // User explicitly opts in to having @EnfernoApp amplify a specific share
  // card on official channels. Per-card, NOT per-account. Default off.
  // cardId should be a deterministic string from the card content so re-shares
  // don't double-record consent.
  const addAmplificationConsent = ({ cardId, type }) => {
    if (!cardId) return;
    setProfile((p) => {
      const existing = p.amplificationConsents || [];
      // Dedup by cardId — if the user re-opens the same card and reconfirms,
      // we keep the original grant timestamp.
      if (existing.some((c) => c.cardId === cardId)) return p;
      return {
        ...p,
        amplificationConsents: [...existing, { cardId, type, grantedAt: new Date().toISOString() }],
      };
    });
  };

  const logPain = (level) => {
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setProfile((p) => ({
      ...p,
      painLog: { ...(p.painLog || {}), [today]: { level: Number(level), time: new Date().toISOString() } },
    }));
  };

  const logWellbeing = (entry) => {
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setProfile((p) => ({
      ...p,
      wellbeingLog: { ...(p.wellbeingLog || {}), [today]: { ...entry, ts: new Date().toISOString() } },
    }));
  };

  // ---------- Display preferences ----------
  // Per-page, per-widget visibility overrides. `value` may be true/false to
  // override, or null to clear the override (fall back to smart default).
  const setDisplayPref = (page, widgetId, value) => {
    setProfile((p) => {
      const dp = { ...(p.displayPrefs || { preset: 'default' }) };
      const pagePrefs = { ...(dp[page] || {}) };
      if (value === null || value === undefined) {
        delete pagePrefs[widgetId];
      } else {
        pagePrefs[widgetId] = !!value;
      }
      dp[page] = pagePrefs;
      return { ...p, displayPrefs: dp };
    });
  };

  const setDisplayPreset = (preset) => {
    setProfile((p) => ({
      ...p,
      displayPrefs: { ...(p.displayPrefs || {}), preset },
    }));
  };

  const resetDisplayPrefs = () => {
    setProfile((p) => ({ ...p, displayPrefs: { preset: p.displayPrefs?.preset || 'default' } }));
  };

  return (
    <ProfileContext.Provider value={{
      profile, history, training,
      update, reset, resetTrainingHistory, completeWorkout, updateWorkout, removeWorkout,
      addTrainingSession, deleteTrainingSession, updateTrainingSession, updateRank, addPromotion, deletePromotion,
      addStreakPause, closeStreakPause, removeStreakPause, markStreakCelebrated, updateStreakBestEver, addProgramCompletion, snoozeProgramCompletion, addAmplificationConsent,
      logPain, logWellbeing, loaded,
      setDisplayPref, setDisplayPreset, resetDisplayPrefs,
      syncStatus,
      historyClamped, historyClampDays,
    }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => useContext(ProfileContext);
